import * as utils from '@/common/utils'
import ApiService from '@/common/api.service'
import * as dt from '@/common/dateUtils'
import * as findVehiclesWidgetHelpers from '@/store/assets/vehicles/findVehiclesWidget.helpers'
import * as findUnitsWidgetHelpers from '@/store/units/findUnitsWidget.helpers'
import * as findVehicleGroupsWidgetHelper from '@/store/assets/vehicle-groups/findVehicleGroupsWidget.helpers'
import * as findEventPoolsWidgetHelpers from '@/store/events/pools/findEventPoolsWidget.helpers'
import * as findPartnersWidgetHelpers from '@/store/administration/partners/findPartnersWidget.helpers'
import * as findAgenciesWidgetHelpers from '@/store/administration/agencies/findAgenciesWidget.helpers'
import * as findClientsWidgetHelpers from '@/store/administration/clients/findClientsWidget.helpers'
import { SEARCH_PREPEND_SEARCH_RESULTS } from '@/store/search/searchModule.types'
import store from '@/store/index'
import * as security from '@/common/security'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    report_type: '',
    report_mode: 'preview', // xlsx, pdf
    search_mode: '',
    isScheduleMode: false,
    scheduleMode: 'none',
    vehicles: [],
    units: [],
    pools: [],
    vehicle_groups: [],
    pool_id: null,
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    filters: {
      comms_status_code: [],
      unit_type_code: [],
      unit_model_code: [],
      unit_status_code: [],
      unit_imei: [],
      unit_serial_no: [],
      unit_name: [],
      event_code: [],
      vehicle_reg_no: [],
      vehicle_fleet_no: [],
      driver_name: [],
      driver_tag_no: [],
      expense_category: [],
      expense_type: [],
      tax_deductible: [] // 'tax_deductible', 'not_tax_deductible'
    },
    date_range_type: 'day', // day, month, range
    date_range_day: dt.dateShort(dt.now()),
    date_range_month: dt.monthShort(dt.now()),
    date_range_from: dt.dateShort(dt.now().startOf('month')),
    date_range_to: dt.dateShort(dt.now()),
    time_range_from: '00:00',
    time_range_to: '23:59',
    order_by: 'reg_date',
    group_by: null,
    summarize_by: 'vehicles',
    sort_order: 'asc',
    pdf_options: 'show_veh_sub',
    pdf_charts: null,
    include_summary: true,
    include_cfo: true,
    schedule: {
      id: null,
      run_interval: 'daily',
      run_time: '18:00',
      period: 'past_24_hours',
      days_of_week: [],
      days_of_month: [],
      enabled: true
    },
    schedules: [],
    vehicleSelectionSet: 'vehicles',
    unitsSelectionSet: 'units',
    poolsSelectionSet: 'pools',
    scheduledVehicleSet: 'rep_scheduled_vehicles',
    scheduledUnitSet: 'rep_scheduled_units',
    scheduledEventPoolSet: 'rep_scheduled_pools',
    scheduledVehicleGroupSet: 'rep_scheduled_vehicle_groups',
    partnerSelectionSet: 'rep_partners',
    agencySelectionSet: 'rep_agencies',
    clientSelectionSet: 'rep_clients',
    schedulePagination: {
      per_page: 14
    }
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param options Object
 *
 * @return Object
 */
function transformReportRunPost (options) {
  let transformedOptions = Object.assign({}, options)

  if (transformedOptions.time_range_from.split(':').length === 2) {
    transformedOptions.time_range_from += ':00'
  }

  if (transformedOptions.time_range_to.split(':').length === 2) {
    transformedOptions.time_range_to += ':00'
  }

  let organizational = getReportOrgIds(transformedOptions)

  return {
    report_type: transformedOptions.report_type,
    report_mode: transformedOptions.report_mode,
    search_mode: transformedOptions.search_mode,
    isScheduleMode: transformedOptions.isScheduleMode,
    vehicles: transformedOptions.vehicles,
    units: transformedOptions.units,
    pools: transformedOptions.pools,
    vehicle_groups: transformedOptions.vehicle_groups,
    partner_id: organizational.partner_id,
    agency_id: organizational.agency_id,
    client_id: organizational.client_id,
    filters: transformedOptions.filters,
    date_range_type: transformedOptions.date_range_type, // day, month, range
    date_range_day: transformedOptions.date_range_day,
    date_range_month: transformedOptions.date_range_month,
    date_range_from: transformedOptions.date_range_from,
    date_range_to: transformedOptions.date_range_to,
    time_range_from: transformedOptions.time_range_from,
    time_range_to: transformedOptions.time_range_to,
    group_by: transformedOptions.group_by,
    order_by: transformedOptions.order_by,
    summarize_by: transformedOptions.summarize_by,
    sort_order: transformedOptions.sort_order,
    pdf_options: transformedOptions.pdf_options,
    pdf_charts: transformedOptions.pdf_charts,
    include_summary: transformedOptions.include_summary,
    include_cfo: transformedOptions.include_cfo
  }
}

/**
 * Unscheduled reports does not require a client to be selected
 * to list assets(Vehicles, units, groups).
 * Report verification does require a client id.
 *
 * @param options
 * @returns {{partner_id: *, agency_id: *, client_id: *}}
 */
function getReportOrgIds (options) {
  let asset = {}

  switch (options.report_type) {
    case 'debrief_detailed':
    case 'vehicle_analysis':
    case 'events_summary':
    case 'vehicle_speeding_detailed':
      asset = getFirstSelectedVehicle(options)
      break
    case 'debrief_summary':
    case 'vehicle_expenses':
    case 'events_pool_detailed':
    case 'events_pool_summary':
    case 'vehicle_speeding_summary':
    case 'vehicle_maintenance':
    case 'status_units_detailed':
    case 'status_units_summary':
      asset = {
        client_id: options.client.id,
        agency_id: options.agency.id,
        partner_id: options.partner.id
      }
      break
    case 'position_log':
    case 'events_detailed':
      if (options.units.length > 0) {
        asset = getFirstSelectedUnit(options)
      } else {
        asset = getFirstSelectedVehicle(options)
      }
      break
    default:
      console.warn('DEBUG: Add a case for the report type to add assets')
  }

  return {
    client_id: asset.client_id,
    agency_id: asset.agency_id,
    partner_id: asset.partner_id
  }
}

function getFirstSelectedVehicle (options) {
  return Object.values(findVehiclesWidgetHelpers.selectedVehicles(options.vehicleSelectionSet))[0]
}

function getFirstSelectedUnit (options) {
  return Object.values(findUnitsWidgetHelpers.selectedUnits(options.unitsSelectionSet))[0]
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

function formDefaults () {
  return {
    partners: [],
    unit_models: [],
    unit_types: [],
    unit_status: [],
    comms_status: [],
    unitTypeModels: [],
    event_codes: [],
    expense_types: []
  }
}

/**
 * Return an array of vehicle ids.
 *
 * @param vehicles
 * @returns []
 */
/*
function getVehicleIds (vehicles) {
  let ids = []

  for (let key in vehicles) {
    if (vehicles.hasOwnProperty(key)) {
      ids.push(vehicles[key].id)
    }
  }

  return ids
}
*/

/**
 * Return an array of vehicle ids and their unit
 * assignments.
 *
 * @param vehicles
 * @returns []
 */
function getVehIdsAndUnitAssigns (vehicles) {
  let results = []

  for (let key in vehicles) {
    if (vehicles.hasOwnProperty(key)) {
      results.push({
        id: vehicles[key].id,
        assigned_as: vehicles[key].unit_assigned_as
      })
    }
  }

  return results
}

/**
 * Return an array of unit ids.
 *
 * @param units
 * @returns []
 */
function getUnitIds (units) {
  let ids = []

  for (let key in units) {
    if (units.hasOwnProperty(key)) {
      ids.push(units[key].id)
    }
  }

  return ids
}

function getPoolIds (pools) {
  let ids = []

  for (let key in pools) {
    if (pools.hasOwnProperty(key)) {
      ids.push(pools[key].id)
    }
  }

  return ids
}

/**
 * Returns an array of vehicle group ids
 *
 * @param vehicleGroups
 * @returns []
 */
function getVehGroupIds (vehicleGroups) {
  let results = []

  for (let key in vehicleGroups) {
    if (vehicleGroups.hasOwnProperty(key)) {
      results.push(vehicleGroups[key].id)
    }
  }

  return results
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  canSaveSchedule (state) {
    if (state.data.schedule.run_interval === 'weekly') {
      return state.data.schedule.days_of_week.length > 0
    } else if (state.data.schedule.run_interval === 'monthly') {
      return state.data.schedule.days_of_month.length > 0
    }

    return true
  },

  schedule (state) {
    if (state.data.schedule.id === null) return null
    const index = state.data.schedules.findIndex((schedule) => schedule.id === state.data.schedule.id)
    return index === -1 ? null : state.data.schedules[index]
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchUnitOptionFilters (context) {
    return ApiService
      .get(`units/options/filters`)
      .then((response) => {
        context.commit('setUnitOptionFilters', response.data.data)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchEventCodes (context) {
    return ApiService
      .get('events/profiles/event/codes')
      .then((response) => {
        context.commit('setEventCodes', response.data.data.event_codes)
      })
  },

  queryExpenseTypesListing (context) {
    const clientId = context.getters['get'].client.id
    return ApiService
      .get(`vehicles/expense/types/by-client/${clientId}/listing`)
      .then((response) => {
        context.commit('populateExpenseTypes', response.data.data)
        return response
      })
  },

  postRunDebriefSummary (context) {
    if (context.getters['get'].search_mode === 'all-groups') {
      context.commit('setVehicleGroupSelection', [])
    } else if (context.getters['get'].search_mode === 'find-groups') {
      const vehicleGroups = findVehicleGroupsWidgetHelper.selectedGroups('vehicle_groups')
      context.commit('setVehicleGroupSelection', getVehGroupIds(vehicleGroups))
    }
    context.commit('setVehicleSelection', [])
    context.commit('setUnitSelection', [])
    context.commit('setPoolSelection', [])
    return context.dispatch('apiPost')
  },

  postRunVehicleAndGroupSelections (context) {
    if (context.getters['get'].search_mode === 'all-vehicles') {
      context.commit('setVehicleGroupSelection', [])
      context.commit('setVehicleSelection', [])
    } else if (context.getters['get'].search_mode === 'find-groups') {
      const vehicleGroups = findVehicleGroupsWidgetHelper.selectedGroups('vehicle_groups')
      context.commit('setVehicleGroupSelection', getVehGroupIds(vehicleGroups))
      context.commit('setVehicleSelection', [])
    } else if (context.getters['get'].search_mode === 'find-vehicles') {
      const vehicles = findVehiclesWidgetHelpers.selectedVehicles('vehicles')
      context.commit('setVehicleSelection', getVehIdsAndUnitAssigns(vehicles))
      context.commit('setVehicleGroupSelection', [])
    }

    context.commit('setUnitSelection', [])
    context.commit('setPoolSelection', [])
    return context.dispatch('apiPost')
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const vehicleGroups = findVehicleGroupsWidgetHelper.selectedGroups('vehicle_groups')
    const vehicleSelectionSet = context.getters['get'].vehicleSelectionSet
    const unitsSelectionSet = context.getters['get'].unitsSelectionSet
    const poolsSelectionSet = context.getters['get'].poolsSelectionSet
    context.commit('setVehicleGroupSelection', getVehGroupIds(vehicleGroups))
    context.commit('setVehicleSelection', getVehIdsAndUnitAssigns(findVehiclesWidgetHelpers.selectedVehicles(vehicleSelectionSet)))
    context.commit('setUnitSelection', getUnitIds(findUnitsWidgetHelpers.selectedUnits(unitsSelectionSet)))
    context.commit('setPoolSelection', getPoolIds(findEventPoolsWidgetHelpers.selectedPools(poolsSelectionSet)))

    return context.dispatch('apiPost')
  },

  apiPost (context) {
    const options = transformReportRunPost(context.getters['get'])
    return ApiService
      .post(`reports/${options.report_type}`, options)
      .then((response) => {
        if (response.data.data.preview) {
          return response
        } else {
          store.commit(SEARCH_PREPEND_SEARCH_RESULTS, { data: response.data.data, categoryName: 'report' })
          return response
        }
      })
  },

  runScheduleNow (context, id) {
    const route = `reports/schedule/run/${id}`
    return ApiService
      .post(route, {})
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    let report = context.getters['get']
    let route = 'reports/status'

    if (report.report_type !== '') {
      route = `reports/status/${report.report_type}`
    }

    return ApiService
      .get(route, params)
      .then((response) => {
        return response
      })
  },

  putSchedule (context, payload) {
    const report = context.getters['get']

    const schedule = {
      ...report.schedule,
      run_time: dt.appendSeconds(report.schedule.run_time),
      report_mode: report.report_mode,
      report_type: report.report_type,
      client_id: report.client.id,
      agency_id: report.agency.id,
      partner_id: report.partner.id,
      ...payload.data
    }

    return ApiService
      .put(`reports/schedule/${report.report_type}/${schedule.id}`, schedule)
      .then((response) => {
        return response
      })
  },

  postSchedule (context, payload) {
    const report = context.getters['get']

    const schedule = {
      ...report.schedule,
      run_time: dt.appendSeconds(report.schedule.run_time),
      report_mode: report.report_mode,
      report_type: report.report_type,
      client_id: report.client.id,
      agency_id: report.agency.id,
      partner_id: report.partner.id,
      ...payload.data
    }

    return ApiService
      .post(`reports/schedule/${report.report_type}`, schedule)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  querySchedules (context, params) {
    const report = context.getters['get']
    const route = `reports/schedule/${report.report_type}`

    let paramsTmp
    if (report.report_type !== 'status_units_summary' && report.report_type !== 'status_units_detailed') {
      const clientId = context.getters['get'].client.id
      paramsTmp = {...params, ...report.schedulePagination, client_id: clientId}
    } else {
      paramsTmp = {...params, ...report.schedulePagination}
    }

    return ApiService
      .get(route, paramsTmp)
      .then((response) => {
        utils.setSearchPagination(response.data.pagination, report.report_type + '_schedule')
        context.commit('setSchedules', response.data.data)
        return response
      })
  },

  deleteSchedule (context, id) {
    const report = context.getters['get']
    const route = `reports/schedule/${report.report_type}/${id}`
    return ApiService
      .delete(route)
      .then((response) => {
        return response
      })
  },

  queryVehiclesWithPrimaryUnits (context, params) {
    const clientId = context.getters['get'].client.id
    params['unit_assigned_as'] = 'primary'
    return ApiService
      .get(`vehicles/by-client/${clientId}/with-units`, params)
      .then((response) => {
        return response
      })
  },

  saveSchedule (context, payload) {
    if (context.getters['get'].schedule.id === null) {
      return context.dispatch('postSchedule', payload).then(() => {
        return context.dispatch('querySchedules').then(() => {
          context.commit('setScheduleMode', 'none')
        })
      })
    } else {
      return context.dispatch('putSchedule', payload).then(() => {
        return context.dispatch('querySchedules').then(() => {
          context.commit('setScheduleMode', 'none')
        })
      })
    }
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  setScheduleMode (state, show) {
    state.data.scheduleMode = show
  },

  setEventCodes (state, data) {
    state.form.event_codes = Object.assign({}, data)
  },

  setVehicleSelection (state, data) {
    state.data.vehicles = data
  },

  setUnitSelection (state, data) {
    state.data.units = data
  },

  setVehicleGroupSelection (state, data) {
    state.data.vehicle_groups = data
  },

  setClientSelection (state, data) {
    if (Object.keys(data).length > 0) {
      state.data.client.id = data[Object.keys(data)[0]].id
    }
  },

  setPoolSelection (state, data) {
    state.data.pools = data
  },

  setUnitOptionFilters (state, data) {
    state.form.unit_models = data.unit_models
    state.form.unit_types = data.unit_types
    state.form.unitTypeModels = data.unit_models
    state.form.unit_status = data.unit_status
    state.form.comms_status = data.comms_status
  },

  filterUnitModelsByTypes (state) {
    let types = state.data.filters.unit_type_code
    state.form.unitTypeModels = state.form.unit_models.filter((model) => {
      return !types.includes(model.unit_type_code)
    })
  },

  populateExpenseTypes (state, expenseTypes) {
    state.form.expense_types = []
    expenseTypes.forEach(expenseType => {
      state.form.expense_types.push(expenseType)
    })
  },

  clearExpenseTypes (state) {
    state.form.expense_types = []
  },

  setSchedules (state, schedules) {
    state.data.schedules = schedules
  },

  setSelectedSchedule (state, schedule_id) {
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    if (typeof schedule !== 'undefined') {
      state.data.schedule.id = schedule.id
      state.data.schedule.run_interval = schedule.run_interval
      state.data.schedule.run_time = dt.trimSeconds(schedule.run_time)
      state.data.schedule.period = schedule.period
      state.data.schedule.days_of_week = schedule.days_of_week === null ? [] : schedule.days_of_week
      state.data.schedule.days_of_month = schedule.days_of_month === null ? [] : schedule.days_of_month
      state.data.schedule.enabled = schedule.enabled
      state.data.report_mode = schedule.options.mode
    }
  },

  setSelectedVehicles (state, schedule_id) {
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    const vehicles = schedule.options.vehicles.map((vehicle) => {
      return {
        id: vehicle.id,
        reg_no: vehicle.reg_no,
        unit_id: vehicle.unit_id,
        fleet_no: vehicle.fleet_no,
        unit_assigned_as: vehicle.assigned_as,
        client_id: vehicle.client_id,
        client: {
          id: vehicle.client_id,
          name: vehicle.client_name
        },
        vehicle_group_id: vehicle.vehicle_group_id,
        vehicle_group: {
          id: vehicle.vehicle_group_id,
          name: vehicle.vehicle_group
        }
      }
    })

    findVehiclesWidgetHelpers.clearSelectedVehicles(state.data.scheduledVehicleSet)
    findVehiclesWidgetHelpers.addSelectedVehicles(state.data.scheduledVehicleSet, vehicles)
  },

  setSelectedUnits (state, schedule_id) {
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    const units = schedule.options.units.map((unit) => {
      return {
        id: unit.id,
        imei: unit.imei,
        serial_no: unit.serial_no,
        unit_assigned_as: unit.assigned_as,
        client_id: unit.client_id,
        client: {
          id: unit.client_id,
          name: unit.client_name
        }
      }
    })

    findUnitsWidgetHelpers.clearSelectedUnits(state.data.scheduledUnitSet)
    findUnitsWidgetHelpers.addSelectedUnits(state.data.scheduledUnitSet, units)
  },

  setSelectedVehicleGroups (state, schedule_id) {
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    const vehicleGroups = schedule.options.vehicle_groups.map((group) => {
      return {
        id: group.id,
        name: group.name
      }
    })
    findVehicleGroupsWidgetHelper.clearSelectedGroups(state.data.scheduledVehicleGroupSet)
    findVehicleGroupsWidgetHelper.addSelectedGroups(state.data.scheduledVehicleGroupSet, vehicleGroups)
  },

  setSelectedEventPools (state, schedule_id) {
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    findEventPoolsWidgetHelpers.clearSelectedPools(state.data.scheduledEventPoolSet)
    findEventPoolsWidgetHelpers.addSelectedPools(state.data.scheduledEventPoolSet, schedule.options.event_pools)
  },

  setSelectedOrg (state, schedule_id) {
    findPartnersWidgetHelpers.clearSelectedPartners(state.data.partnerSelectionSet)
    findAgenciesWidgetHelpers.clearSelectedAgencies(state.data.agencySelectionSet)
    findClientsWidgetHelpers.clearSelectedClients(state.data.clientSelectionSet)
    const schedule = state.data.schedules.find((schedule) => schedule.id === schedule_id)
    store.commit('reports/reportsModule/setAssignmentsForSchedule', schedule)
    if (schedule.client.id !== null) {
      findClientsWidgetHelpers.addSelectedClients(state.data.clientSelectionSet, [{
        id: schedule.client.id,
        name: schedule.client.name
      }])
    } else if (schedule.agency.id !== null) {
      findAgenciesWidgetHelpers.addSelectedAgencies(state.data.agencySelectionSet, [{
        id: schedule.agency.id,
        name: schedule.agency.name
      }])
    } else if (schedule.partner.id !== null) {
      findPartnersWidgetHelpers.addSelectedPartners(state.data.partnerSelectionSet, [{
        id: schedule.partner.id,
        name: schedule.partner.name
      }])
    }
  },

  setScheduleFilters (state, {key, value}) {
    state.data.filters[key] = value
  },

  resetScheduleState (state) {
    store.commit('reports/reportsModule/clearSchedule')
    state.data.schedules = []
    state.data.isScheduleMode = false
    state.data.scheduleMode = 'none'
    store.commit('reports/reportsModule/resetScheduleAssignments')
  },

  resetScheduleAssignments (state) {
    findVehiclesWidgetHelpers.clearSelectedVehicles(state.data.scheduledVehicleSet)
    findVehicleGroupsWidgetHelper.clearSelectedGroups(state.data.scheduledVehicleGroupSet)
    findUnitsWidgetHelpers.clearSelectedUnits(state.data.scheduledUnitSet)
    findEventPoolsWidgetHelpers.clearSelectedPools(state.data.scheduledEventPoolSet)
    findPartnersWidgetHelpers.clearSelectedPartners(state.data.partnerSelectionSet)
    findAgenciesWidgetHelpers.clearSelectedAgencies(state.data.agencySelectionSet)
    findClientsWidgetHelpers.clearSelectedClients(state.data.clientSelectionSet)
  },

  clearSchedule (state) {
    state.data.schedule.id = null
    state.data.schedule.run_interval = 'daily'
    state.data.schedule.run_time = '18:00'
    state.data.schedule.period = 'past_24_hours'
    state.data.schedule.days_of_week = []
    state.data.schedule.days_of_month = []
    state.data.schedule.enabled = true
    state.data.report_mode = 'pdf'
  },

  clearScheduleVehicles (state) {
    findVehiclesWidgetHelpers.clearSelectedVehicles(state.data.scheduledVehicleSet)
  },

  clearScheduleVehicleGroups (state) {
    findVehicleGroupsWidgetHelper.clearSelectedGroups(state.data.scheduledVehicleGroupSet)
  },

  clearScheduleUnits (state) {
    findUnitsWidgetHelpers.clearSelectedUnits(state.data.scheduledUnitSet)
  },

  clearScheduleEventPools (state) {
    findEventPoolsWidgetHelpers.clearSelectedPools(state.data.scheduledEventPoolSet)
  },

  setAssignmentsForSchedule (state, schedule) {
    state.data.client.id = schedule.client.id
    state.data.client.name = schedule.client.name
    state.data.agency.id = schedule.agency.id
    state.data.agency.name = schedule.agency.name
    state.data.partner.id = schedule.partner.id
    state.data.partner.name = schedule.partner.name
  },

  /**
   * Clear the unit's partner, agency and client data.
   *
   * @param state
   */
  clearAssignments (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  clearSelectedAssets (state) {
    findVehiclesWidgetHelpers.clearSelectedVehicles(state.data.scheduledVehicleSet)
    findVehicleGroupsWidgetHelper.clearSelectedGroups(state.data.scheduledVehicleGroupSet)
    findUnitsWidgetHelpers.clearSelectedUnits(state.data.scheduledUnitSet)
  },

  /**
   * Set the report partner, agency and client id's.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('reports/reportsModule/clearAssignments')
    // When editing a unit status scheduled report, the selected client is
    // added to the clientFindWidgetHelper set, but only the client ID and
    // name are stored, as this is how the data is stored in the unit status
    // report options.
    // This incomplete data causes a console error when switching to different
    // reports, which then try to set the selected clients partner/agency.
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner ? client.partner.id : null
      state.data.partner.name = client.partner ? client.partner.name : null
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency ? client.agency.id : null
      state.data.agency.name = client.agency ? client.agency.name : null
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Set the report partner and agency id's.
   *
   * @param state
   * @param agency
   */
  setAgency (state, agency) {
    store.commit('reports/reportsModule/clearAssignments')
    // Check comment in setClient
    if (security.isAbovePartner()) {
      state.data.partner.id = agency.partner ? agency.partner.id : null
      state.data.partner.name = agency.partner ? agency.partner.name : null
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = agency.id
      state.data.agency.name = agency.name
    }
  },

  /**
   * Set the report partner id and name.
   *
   * @param state
   * @param partner
   */
  setPartner (state, partner) {
    store.commit('reports/reportsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = partner.id
      state.data.partner.name = partner.name
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    utils.pushProp(state.data, name, value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  },

  /**
   * Handles a report_status event from an event source connection.
   * @see vue/src/common/sse.js
   *
   * @param state Object
   * @param data Object
   */
  handleReportStatusEvent (state, data) {
    const reportStatus = data.data
    const searchResults = utils.getSearchResults('report')
    let report = searchResults.find((result) => {
      return result.id === reportStatus.id
    })

    if (report) {
      report.status = reportStatus.status
      report.status_text = reportStatus.status_text
      report.progress = reportStatus.progress
      report.url = reportStatus.url
      report.completed_at = reportStatus.completed_at
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/reports/reportsModule.js